import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCalculator, faArrowRight, faDatabase, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import HomeNicheSearch from '../components/HomeNicheSearch';
import '../css/pages/Tools.css';

interface ToolsProps {
    // True once the logged-in user can actually open /sponsors (subscribed
    // or admin). Undefined/false covers logged-out visitors too, who keep
    // seeing today's locked-preview teaser.
    canAccessSponsors?: boolean;
}

const NICHE_MULTIPLIERS: Record<string, number> = {
    'Finance/Business': 1.3,
    'Tech/AI/SaaS': 1.3,
    'Health/Wellness': 1.1,
    'Marketing/Growth': 1.15,
    'General/Lifestyle': 1.0,
    'Hobby/Niche Interest': 0.9,
};

const BASE_CPM = { mention: 20, banner: 28, dedicated: 65 };

const RateCalculator: React.FC = () => {
    const [audience, setAudience] = useState<number>(10000);
    const [engagementRate, setEngagementRate] = useState<number>(30);
    const [niche, setNiche] = useState<string>('General/Lifestyle');

    const engagementMultiplier = engagementRate > 40 ? 1.2 : engagementRate < 20 ? 0.8 : 1.0;
    const nicheMultiplier = NICHE_MULTIPLIERS[niche] || 1.0;
    const combined = (audience / 1000) * engagementMultiplier * nicheMultiplier;

    const rate = (base: number) => {
        const mid = base * combined;
        return { low: Math.round(mid * 0.8), high: Math.round(mid * 1.2) };
    };

    const mention = rate(BASE_CPM.mention);
    const banner = rate(BASE_CPM.banner);
    const dedicated = rate(BASE_CPM.dedicated);

    return (
        <div className="tools-calculator">
            <div className="tools-calculator__inputs">
                <label>
                    Subscriber count
                    <input
                        type="number"
                        min={100}
                        value={audience}
                        onChange={(e) => setAudience(Math.max(0, Number(e.target.value)))}
                    />
                </label>
                <label>
                    Open/engagement rate (%)
                    <input
                        type="number"
                        min={1}
                        max={100}
                        value={engagementRate}
                        onChange={(e) => setEngagementRate(Math.max(1, Math.min(100, Number(e.target.value))))}
                    />
                </label>
                <label>
                    Niche
                    <select value={niche} onChange={(e) => setNiche(e.target.value)}>
                        {Object.keys(NICHE_MULTIPLIERS).map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="tools-calculator__results">
                <div className="tools-calculator__result-card">
                    <span className="tools-calculator__result-label">Newsletter mention</span>
                    <span className="tools-calculator__result-value">${mention.low}–${mention.high}</span>
                </div>
                <div className="tools-calculator__result-card">
                    <span className="tools-calculator__result-label">Banner / dedicated slot</span>
                    <span className="tools-calculator__result-value">${banner.low}–${banner.high}</span>
                </div>
                <div className="tools-calculator__result-card">
                    <span className="tools-calculator__result-label">Dedicated send</span>
                    <span className="tools-calculator__result-value">${dedicated.low}–${dedicated.high}</span>
                </div>
            </div>
            <p className="tools-calculator__disclaimer">
                Rough estimates based on general newsletter sponsorship benchmarks — not a quote or guarantee. Actual deals vary by sponsor, niche demand, and placement quality.
            </p>
        </div>
    );
};

const Tools: React.FC<ToolsProps> = ({ canAccessSponsors = false }) => {
    return (
        <div className="web-page">
            <Helmet>
                <title>Free Tools for Newsletter Creators — Sponsor Search & Rate Calculator | SponsorDB</title>
                <meta name="description" content="Free tools for newsletter creators: search real newsletter sponsors by niche with no account needed, and calculate what to charge for a sponsorship slot based on your audience size." />
                <link rel="canonical" href="https://sponsor-db.com/tools" />
            </Helmet>
            <div className="web-section web-section-content" id="tools-page">
                <div className="web-section__container">
                    <div className="tools-header">
                        <h1>{canAccessSponsors ? 'Tools' : 'Free Tools for Newsletter Creators'}</h1>
                        <p>
                            {canAccessSponsors
                                ? 'Everything below is included with your subscription.'
                                : 'No account needed for any of this — subscribe only when you want the full database.'}
                        </p>
                    </div>

                    {canAccessSponsors ? (
                        <section id="free-sponsors" className="tools-section">
                            <h2><FontAwesomeIcon icon={faDatabase} /> Sponsor Database</h2>
                            <p className="tools-section__intro">
                                You already have full access — search and filter every sponsor in the database from there.
                            </p>
                            <Link to="/sponsors" className="btn btn-primary tools-section__cta">
                                Browse the sponsor database <FontAwesomeIcon icon={faArrowRight} />
                            </Link>
                        </section>
                    ) : (
                        <section id="free-sponsors" className="tools-section">
                            <h2><FontAwesomeIcon icon={faSearch} /> Search Real Sponsors by Niche</h2>
                            <p className="tools-section__intro">Pick your niche — see the actual companies sponsoring newsletters like yours. Full contact emails unlock with a subscription.</p>
                            <HomeNicheSearch />
                        </section>
                    )}

                    <section id="rate-calculator" className="tools-section">
                        <h2><FontAwesomeIcon icon={faCalculator} /> Sponsorship Rate Calculator</h2>
                        <p className="tools-section__intro">Get a ballpark on what to charge sponsors based on your audience.</p>
                        <RateCalculator />
                    </section>

                    <section id="weekly-report" className="tools-section">
                        <h2><FontAwesomeIcon icon={faEnvelope} /> The Weekly Sponsor Report</h2>
                        <p className="tools-section__intro">
                            Free, every week: how many new sponsors we verified, which niches are hottest, and what the newsletters landing those deals have in common.
                        </p>
                        <Link to="/newsletter/" className="btn btn-primary tools-section__cta">
                            Read the Weekly Sponsor Report <FontAwesomeIcon icon={faArrowRight} />
                        </Link>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default Tools;
